import AboutWrapper from '../AboutWrapper/AboutWrapper';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { STPaginationButton, StBox, StRichText } from './Glossary.styles';
import { NextImage, RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { StNextImage } from '../shared/NextImage.styles';
import UiHeading from '../shared/Heading';
import UiText from '../shared/Typography/UiText';
import { bumpStudySwitcherTrigger } from '../../features/studySwitcherSlice';

const fallBackTabItem: any = {
  id: '',
  url: '',
  name: '',
  displayName: '',
  fields: {
    link: { value: { href: '' } },
  },
};

const GlossaryContent: React.FC<any> = (props) => {
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = useState('');
  const [filteredGlossaryWords, setFilteredGlossaryWords] = useState<any[]>([]);
  const [alphabetList, setAlphabetList] = useState<string[]>([]);
  const tabLinks = props?.fields?.tabLinks || [fallBackTabItem];
  const activeTab = props?.fields?.activeTab || fallBackTabItem;
  const icon = props?.fields?.icon;
  const iconColor = props?.fields?.iconColor?.value || '';
  const title = props?.fields?.title;
  const content = props?.fields?.content;
  const showNumericWordsFirst = props?.fields?.showNumericWordsFirst?.value;
  const [combinedlistOfGlossary, setCombinedlistOfGlossary] = useState<any[]>([]);
  const sortAlphabetically = (wordList: any[]) => {
    return wordList.sort((a, b) => {
      const wordA = a?.fields?.heading?.value?.toLowerCase();
      const wordB = b?.fields?.heading?.value?.toLowerCase();
      if (wordA < wordB) return -1;
      if (wordA > wordB) return 1;
      return 0;
    });
  };

  useEffect(() => {
    setSelectedOption(props?.fields?.showAllText?.value.toLowerCase() || 'all');
  }, [props?.fields?.showAllText]);

  useEffect(() => {
    const listOfGlossaryWords = props?.fields?.listOfGlossaryAlphaWords || [];
    const listOfGlossaryNumericWords = props?.fields?.listOfGlossaryNumericWords || [];

    const result = showNumericWordsFirst
      ? [
          ...sortAlphabetically(listOfGlossaryNumericWords),
          ...sortAlphabetically(listOfGlossaryWords),
        ]
      : [
          ...sortAlphabetically(listOfGlossaryWords),
          ...sortAlphabetically(listOfGlossaryNumericWords),
        ];

    setCombinedlistOfGlossary(result);
  }, [
    props?.fields?.listOfGlossaryAlphaWords,
    props?.fields?.listOfGlossaryNumericWords,
    showNumericWordsFirst,
  ]);

  useEffect(() => {
    const all = [
      props?.fields?.showAllText?.value,
      ...combinedlistOfGlossary.map((item: any) => {
        return item?.fields?.heading?.value?.charAt(0).toUpperCase();
      }),
    ];

    setAlphabetList([...new Set(all)]);
  }, [combinedlistOfGlossary, props?.fields?.showAllText]);

  useEffect(() => {
    const result: any = combinedlistOfGlossary.filter((item: any) => {
      if (selectedOption.toLowerCase() === 'all') {
        return true;
      }
      return item?.fields?.heading?.value?.toLowerCase().startsWith(selectedOption.toLowerCase());
    });
    setFilteredGlossaryWords(result);
  }, [combinedlistOfGlossary, selectedOption]);

  // Bump after glossary content is ready (initial load + alphabet filter).
  useEffect(() => {
    dispatch(bumpStudySwitcherTrigger());
  }, [filteredGlossaryWords, dispatch]);

  const AlphabetPagination = ({ onClick }: any) => {
    return (
      <Grid>
        {alphabetList.map((letter) => (
          <STPaginationButton
            className={selectedOption.toLowerCase() === letter.toLowerCase() ? 'active' : ''}
            key={letter}
            onClick={() => {
              onClick(letter);
            }}
          >
            {letter}
          </STPaginationButton>
        ))}
      </Grid>
    );
  };

  const handleAlphabetPagination = (letter: string) => {
    setSelectedOption(letter);
  };

  return (
    <AboutWrapper tabLinks={tabLinks} activeTab={activeTab}>
      <Grid id="content-with-filter">
        <Grid id="content">
          <StBox>
            {iconColor ? (
              <StNextImage field={icon} overrideIconColor={iconColor} />
            ) : (
              <NextImage field={icon} />
            )}
            <UiHeading size="h2">{title}</UiHeading>
          </StBox>
          <StRichText field={content} />
        </Grid>
        <Grid id="alphabet">
          <AlphabetPagination onClick={handleAlphabetPagination} />
        </Grid>
      </Grid>
      <Grid id="articles">
        {filteredGlossaryWords.map((article: any, index: number) => (
          <div key={index}>
            <Grid aria-label="glossary item">
              <UiHeading
                size="h3"
                aria-label={article?.fields?.heading?.value}
                id="ui-glossary-subheading"
              >
                {article?.fields?.heading}
              </UiHeading>
              <UiText>
                <RichText field={article?.fields?.content} />
              </UiText>
            </Grid>
            <Divider sx={{ marginBottom: '30px' }} />
          </div>
        ))}
        {filteredGlossaryWords.length === 0 && (
          <>
            <Grid>
              <Typography variant="h5">No Glossary Found</Typography>
            </Grid>
          </>
        )}
      </Grid>
    </AboutWrapper>
  );
};

export default GlossaryContent;
